/**
 * Mesma lógica do MainLayout para não lidas do inbox dinâmico (Z-API / Cloud API),
 * restrita a inboxes com PURPOSE=platform_support + contagem de chamados ativos no hub.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { io, Socket } from 'socket.io-client';
import { getWebSocketUrl } from '../config/environment';
import { inboxService } from '../services/inboxService';
import {
  getPlatformSupportTicketStats,
  listPlatformSupportWhatsappInboxes,
  PlatformSupportWhatsappInbox,
  syncPlatformSupportTicketsInbox,
} from '../services/platformSupportInbox';
import { inboxConversationCache } from '../services/inboxConversationCache';

const POLL_MS = 35_000;
const DEBOUNCE_MS = 280;
const TICKET_SYNC_MIN_INTERVAL_MS = 30_000;

export interface PlatformSupportHubRealtime {
  whatsappUnreadTotal: number;
  whatsappUnreadByChannel: Record<string, number>;
  openTicketsTotal: number;
  ticketStats: { open: number; in_progress: number; waiting_customer: number };
  inboxes: PlatformSupportWhatsappInbox[];
  refresh: () => Promise<void>;
}

function inboxListSignature(list: PlatformSupportWhatsappInbox[]): string {
  return list
    .map((i) => `${i.id}:${i.channelId || ''}:${i.inboxSlug}`)
    .sort()
    .join('|');
}

export function usePlatformSupportHubRealtime(enabled: boolean): PlatformSupportHubRealtime {
  const [inboxes, setInboxes] = useState<PlatformSupportWhatsappInbox[]>([]);
  const [whatsappUnreadByChannel, setWhatsappUnreadByChannel] = useState<Record<string, number>>({});
  const [openTicketsTotal, setOpenTicketsTotal] = useState(0);
  const [ticketStats, setTicketStats] = useState({ open: 0, in_progress: 0, waiting_customer: 0 });

  const channelIds = useMemo(() => {
    const ids: string[] = [];
    for (const i of inboxes) {
      if (i.channelId && !ids.includes(i.channelId)) ids.push(i.channelId);
    }
    ids.sort();
    return ids;
  }, [inboxes]);

  /** Estável entre refreshes — evita remontar WebSocket quando só a referência do array muda. */
  const channelIdsKey = channelIds.join('|');

  const channelIdsRef = useRef<string[]>(channelIds);
  channelIdsRef.current = channelIds;

  const refreshRef = useRef<() => Promise<void>>(async () => undefined);
  const debounceRef = useRef<number | null>(null);
  const socketRef = useRef<Socket | null>(null);
  const refreshInFlightRef = useRef(false);
  const refreshQueuedRef = useRef(false);
  const lastTicketSyncAtRef = useRef(0);

  const refresh = useCallback(async () => {
    if (!enabled) return;
    if (refreshInFlightRef.current) {
      refreshQueuedRef.current = true;
      return;
    }
    refreshInFlightRef.current = true;
    try {
      const [waRes, unreadRes, statsRes] = await Promise.allSettled([
        listPlatformSupportWhatsappInboxes(),
        inboxService.getUnreadCounts({ purpose: 'platform_support' }),
        getPlatformSupportTicketStats(),
      ]);
      const waList = waRes.status === 'fulfilled' ? waRes.value : null;
      if (waList) {
        setInboxes((prev) => (inboxListSignature(prev) === inboxListSignature(waList) ? prev : waList));
      }
      if (unreadRes.status === 'fulfilled') {
        setWhatsappUnreadByChannel(unreadRes.value.byChannel || {});
      }
      if (statsRes.status === 'fulfilled') {
        const stats = statsRes.value;
        setOpenTicketsTotal(stats.total_active);
        setTicketStats({
          open: stats.open,
          in_progress: stats.in_progress,
          waiting_customer: stats.waiting_customer,
        });
        // Chamado em "in_progress" pode apontar para conversa que não existe nesta base;
        // o backend só materializa os que não têm conversa local.
        const now = Date.now();
        if (stats.total_active > 0 && waList?.length && now - lastTicketSyncAtRef.current > TICKET_SYNC_MIN_INTERVAL_MS) {
          lastTicketSyncAtRef.current = now;
          void syncPlatformSupportTicketsInbox()
            .then((result) => {
              if (result.skipped || result.errors.length) {
                console.warn('[platform-support] chamados não sincronizados com o inbox:', result.skipped || result.errors);
              }
            })
            .catch(() => undefined);
        }
      }
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('platform-support:hub-refresh'));
      }
    } finally {
      refreshInFlightRef.current = false;
      if (refreshQueuedRef.current) {
        refreshQueuedRef.current = false;
        void refreshRef.current();
      }
    }
  }, [enabled]);

  refreshRef.current = refresh;

  const scheduleRefresh = useCallback(() => {
    if (debounceRef.current) window.clearTimeout(debounceRef.current);
    debounceRef.current = window.setTimeout(() => {
      debounceRef.current = null;
      void refreshRef.current();
    }, DEBOUNCE_MS);
  }, []);

  useEffect(() => {
    if (!enabled) return;
    void refresh();
  }, [enabled, refresh]);

  useEffect(() => {
    if (!enabled) return;
    const id = window.setInterval(() => void refreshRef.current(), POLL_MS);
    return () => window.clearInterval(id);
  }, [enabled]);

  useEffect(() => {
    if (!enabled) return;
    const onRefresh = () => scheduleRefresh();
    window.addEventListener('inbox:menu-unread-refresh', onRefresh);
    window.addEventListener('focus', onRefresh);
    const onVisible = () => {
      if (document.visibilityState === 'visible') scheduleRefresh();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.removeEventListener('inbox:menu-unread-refresh', onRefresh);
      window.removeEventListener('focus', onRefresh);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [enabled, scheduleRefresh]);

  useEffect(() => {
    if (!enabled || !channelIdsKey) {
      if (socketRef.current) {
        socketRef.current.removeAllListeners();
        socketRef.current.disconnect();
        socketRef.current = null;
      }
      return;
    }

    const wsUrl = getWebSocketUrl();
    const token = localStorage.getItem('token') || sessionStorage.getItem('token');
    if (!wsUrl?.trim() || !token) return;

    const socket = io(wsUrl, {
      transports: ['websocket', 'polling'],
      timeout: 20000,
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 10000,
      reconnectionAttempts: Infinity,
      auth: { token },
    });
    socketRef.current = socket;

    let connectedBefore = false;
    const subscribe = () => {
      channelIdsRef.current.forEach((channelId) => socket.emit('subscribe_channel', channelId));
      // Só em reconexão: eventos perdidos enquanto offline. Na 1ª conexão o refresh já ocorreu.
      if (connectedBefore) scheduleRefresh();
      connectedBefore = true;
    };

    const bump = (payload: unknown) => {
      const p = payload as Record<string, unknown>;
      const envelope = p?.update ? p : null;
      const message = (p?.message || p) as Record<string, unknown>;
      const payloadChannelId =
        message?.channel_id ||
        (message?.channel as { id?: string } | undefined)?.id ||
        (envelope as { update?: { channel_id?: string }; channel_id?: string } | null)?.update?.channel_id ||
        (envelope as { channel_id?: string } | null)?.channel_id ||
        null;
      const ids = channelIdsRef.current;
      if (payloadChannelId && !ids.includes(String(payloadChannelId))) return;
      scheduleRefresh();
      inboxConversationCache.handleRealtimeEvent(payload);
    };

    socket.on('connect', subscribe);
    socket.on('reconnect', subscribe);
    socket.on('new_message', bump);
    socket.on('conversation_updated', bump);

    return () => {
      socket.off('connect', subscribe);
      socket.off('reconnect', subscribe);
      socket.off('new_message', bump);
      socket.off('conversation_updated', bump);
      socket.disconnect();
      if (socketRef.current === socket) socketRef.current = null;
    };
  }, [enabled, channelIdsKey, scheduleRefresh]);

  const whatsappUnreadTotal = useMemo(
    () => Object.values(whatsappUnreadByChannel).reduce((s, n) => s + (Number(n) || 0), 0),
    [whatsappUnreadByChannel],
  );

  return {
    whatsappUnreadTotal,
    whatsappUnreadByChannel,
    openTicketsTotal,
    ticketStats,
    inboxes,
    refresh,
  };
}
